import React, { useEffect, useState } from "react";
import { X, Play, Pause, Volume2, VolumeX, ExternalLink } from "lucide-react";

export default function VideoModal({ project, onClose }) {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!project) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-[#2E2621]/95 backdrop-blur-sm flex items-center justify-center p-4">
      <button
        onClick={onClose}
        className="absolute top-5 right-5 w-11 h-11 rounded-full bg-[#FAF5EF]/10 hover:bg-[#B0603F] text-[#FAF5EF] flex items-center justify-center transition-colors"
      >
        <X size={22} />
      </button>

      <div className="flex flex-col items-center">
        {/* vertical 9:16 player */}
        <div className="relative w-[300px] h-[533px] md:w-[340px] md:h-[604px] rounded-3xl overflow-hidden shadow-2xl bg-black">
          <img
            src={project.cover}
            alt={project.title}
            className={`w-full h-full object-cover transition-all duration-700 ${
              playing ? "scale-105" : "scale-100 brightness-90"
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />

          {/* center play */}
          {!playing && (
            <button
              onClick={() => {
                if (project.videoUrl) {
                  window.open(project.videoUrl, "_blank", "noopener,noreferrer");
                } else {
                  setPlaying(true);
                }
              }}
              className="absolute inset-0 flex items-center justify-center group"
            >
              <span className="w-20 h-20 rounded-full bg-[#FAF5EF]/90 flex items-center justify-center text-[#3A302A] group-hover:scale-110 group-hover:bg-[#B0603F] group-hover:text-[#FAF5EF] transition-all duration-300 shadow-xl">
                <Play size={32} fill="currentColor" />
              </span>
            </button>
          )}

          {/* top label */}
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
            <span className="font-body text-[10px] tracking-luxe uppercase text-white/90 bg-black/30 px-3 py-1.5 rounded-full backdrop-blur-sm">
              {project.category}
            </span>
            {playing && (
              <span className="flex items-center gap-1.5 font-body text-[10px] text-white bg-red-500/80 px-3 py-1.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE PREVIEW
              </span>
            )}
          </div>

          {/* bottom controls */}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <h3 className="font-serif-display text-2xl text-white mb-1">{project.title}</h3>
            <p className="font-body text-xs text-white/80 mb-4">{project.place}</p>
            {playing && (
              <div className="flex items-center gap-4">
                <button onClick={() => setPlaying(false)} className="text-white hover:text-[#C88A63]">
                  <Pause size={20} />
                </button>
                <div className="flex-1 h-1 rounded-full bg-white/25 overflow-hidden">
                  <div className="h-full bg-[#C88A63] animate-[grow_8s_linear]" style={{ width: "40%" }} />
                </div>
                <button onClick={() => setMuted(!muted)} className="text-white hover:text-[#C88A63]">
                  {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </button>
              </div>
            )}
          </div>
        </div>

        {project.videoUrl ? (
          <a
            href={project.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FAF5EF] text-[#3A302A] text-sm tracking-wide-2 uppercase hover:bg-[#C88A63] hover:text-[#FAF5EF] transition-colors duration-300"
          >
            <ExternalLink size={16} />
            Regarder sur TikTok
          </a>
        ) : (
          <p className="mt-5 font-body text-sm text-[#E7D8C6] text-center max-w-xs">
            Aperçu de la vidéo UGC — la version complète sera bientôt disponible.
          </p>
        )}
      </div>
    </div>
  );
}
